
import { useState, useMemo } from 'react';
import { ShareModal } from './ShareModal';

interface PollOption {
  id: string;
  text: string;
  votes: number;
}

interface VoteTrend {
  date: string;
  votes: Record<string, number>;
  totalVotes: number;
}

interface PollResultsAnalyticsProps {
  isOpen: boolean;
  onClose: () => void;
  pollTitle: string;
  pollId: string;
  options: PollOption[];
  totalVotes: number;
  createdAt: string;
  endDate?: string | null;
  allowMultiple?: boolean;
  voteTrends?: VoteTrend[];
}

export default function PollResultsAnalytics({
  isOpen,
  onClose,
  pollTitle,
  pollId,
  options,
  totalVotes,
  createdAt,
  endDate,
  allowMultiple = false,
  voteTrends: providedTrends
}: PollResultsAnalyticsProps) {
  const [activeView, setActiveView] = useState<'overview' | 'trends' | 'breakdown'>('overview');
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareType, setShareType] = useState<'results' | 'image'>('results');

  // Generate mock vote trends if not provided
  const voteTrends = useMemo(() => {
    if (providedTrends && providedTrends.length > 0) return providedTrends;

    // Generate 7 days of mock trend data
    const trends: VoteTrend[] = [];
    const now = new Date();
    let cumulativeVotes: Record<string, number> = {};
    
    options.forEach(opt => {
      cumulativeVotes[opt.id] = 0;
    });

    for (let i = 6; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      
      const dayVotes: Record<string, number> = {};
      let dayTotal = 0;

      options.forEach(opt => {
        // Distribute votes across days with some randomness
        const baseVotes = Math.floor(opt.votes / 7);
        const variance = Math.floor(Math.random() * (baseVotes * 0.5));
        const votes = i === 0 
          ? opt.votes - cumulativeVotes[opt.id] 
          : baseVotes + (Math.random() > 0.5 ? variance : -variance);
        
        dayVotes[opt.id] = Math.max(0, votes);
        cumulativeVotes[opt.id] += dayVotes[opt.id];
        dayTotal += dayVotes[opt.id];
      });

      trends.push({
        date: date.toISOString().split('T')[0],
        votes: dayVotes,
        totalVotes: dayTotal
      });
    }

    return trends;
  }, [options, providedTrends]);

  // Calculate statistics
  const stats = useMemo(() => {
    const sortedOptions = [...options].sort((a, b) => b.votes - a.votes);
    const leader = sortedOptions[0];
    const runnerUp = sortedOptions[1];
    
    const leaderPercentage = totalVotes > 0 ? (leader.votes / totalVotes) * 100 : 0;
    const runnerUpPercentage = totalVotes > 0 && runnerUp ? (runnerUp.votes / totalVotes) * 100 : 0;
    const leadMargin = leaderPercentage - runnerUpPercentage;

    // Calculate engagement rate (mock)
    const engagementRate = Math.min(95, 45 + Math.random() * 30);

    // Calculate peak voting day
    const peakDay = voteTrends.reduce((max, trend) => 
      trend.totalVotes > max.totalVotes ? trend : max, voteTrends[0]);

    // Calculate average daily votes
    const avgDailyVotes = totalVotes / Math.max(1, voteTrends.length);

    return {
      leader,
      runnerUp,
      leaderPercentage,
      leadMargin,
      engagementRate,
      peakDay,
      avgDailyVotes,
      sortedOptions
    };
  }, [options, totalVotes, voteTrends]);

  // Generate shareable image data
  const generateShareableContent = () => {
    const resultsText = options
      .sort((a, b) => b.votes - a.votes)
      .map((opt, i) => {
        const percentage = totalVotes > 0 ? Math.round((opt.votes / totalVotes) * 100) : 0;
        return `${i + 1}. ${opt.text}: ${percentage}%`;
      })
      .join('\n');

    return {
      title: `Poll Results: ${pollTitle}`,
      text: `📊 Poll Results\n\n${pollTitle}\n\n${resultsText}\n\n👥 ${totalVotes} total votes`,
      url: `${window.location.origin}/forum?poll=${pollId}`
    };
  };

  const handleShareResults = () => {
    setShareType('results');
    setShowShareModal(true);
  };

  const handleShareToTwitter = () => {
    const content = generateShareableContent();
    const tweetText = `📊 ${pollTitle}\n\n🏆 Leading: ${stats.leader.text} (${Math.round(stats.leaderPercentage)}%)\n👥 ${totalVotes} votes\n\nVote now!`;
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(content.url)}`,
      '_blank'
    );
  };

  const handleShareToFacebook = () => {
    const content = generateShareableContent();
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(content.url)}&quote=${encodeURIComponent(content.title)}`,
      '_blank'
    );
  };

  const handleShareToWhatsApp = () => {
    const content = generateShareableContent();
    window.open(
      `https://wa.me/?text=${encodeURIComponent(content.text + '\n\n' + content.url)}`,
      '_blank'
    );
  };

  const handleCopyResults = async () => {
    const content = generateShareableContent();
    try {
      await navigator.clipboard.writeText(content.text + '\n\n' + content.url);
      // Show success notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-emerald-500 text-white px-4 py-2 rounded-lg shadow-lg z-[60] transition-all duration-300';
      notification.innerHTML = '<i class="ri-check-line mr-2"></i>Results copied to clipboard!';
      document.body.appendChild(notification);
      setTimeout(() => {
        notification.classList.add('opacity-0');
        setTimeout(() => document.body.removeChild(notification), 300);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDownloadImage = () => {
    // Create a canvas-based image of the results
    const canvas = document.createElement('canvas');
    canvas.width = 800;
    canvas.height = 600;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) return;

    // Background
    const gradient = ctx.createLinearGradient(0, 0, 800, 600);
    gradient.addColorStop(0, '#3b82f6');
    gradient.addColorStop(1, '#06b6d4');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 800, 600);

    // White card
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.roundRect(40, 40, 720, 520, 20);
    ctx.fill();

    // Title
    ctx.fillStyle = '#1f2937';
    ctx.font = 'bold 28px system-ui';
    ctx.fillText('📊 Poll Results', 70, 100);

    // Poll question
    ctx.fillStyle = '#374151';
    ctx.font = '20px system-ui';
    const maxWidth = 660;
    const words = pollTitle.split(' ');
    let line = '';
    let y = 150;
    
    for (const word of words) {
      const testLine = line + word + ' ';
      const metrics = ctx.measureText(testLine);
      if (metrics.width > maxWidth && line !== '') {
        ctx.fillText(line, 70, y);
        line = word + ' ';
        y += 30;
      } else {
        line = testLine;
      }
    }
    ctx.fillText(line, 70, y);

    // Results bars
    y += 50;
    const sortedOpts = [...options].sort((a, b) => b.votes - a.votes);
    
    sortedOpts.slice(0, 5).forEach((opt, i) => {
      const percentage = totalVotes > 0 ? (opt.votes / totalVotes) * 100 : 0;
      const barWidth = (percentage / 100) * 600;

      // Bar background
      ctx.fillStyle = '#f3f4f6';
      ctx.beginPath();
      ctx.roundRect(70, y, 600, 40, 8);
      ctx.fill();

      // Bar fill
      const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
      ctx.fillStyle = colors[i % colors.length];
      ctx.beginPath();
      ctx.roundRect(70, y, Math.max(barWidth, 10), 40, 8);
      ctx.fill();

      // Option text
      ctx.fillStyle = '#1f2937';
      ctx.font = '14px system-ui';
      ctx.fillText(`${opt.text.substring(0, 30)}${opt.text.length > 30 ? '...' : ''}`, 80, y + 26);

      // Percentage
      ctx.fillStyle = '#6b7280';
      ctx.font = 'bold 14px system-ui';
      ctx.fillText(`${Math.round(percentage)}%`, 690, y + 26);

      y += 55;
    });

    // Total votes
    ctx.fillStyle = '#6b7280';
    ctx.font = '16px system-ui';
    ctx.fillText(`👥 ${totalVotes.toLocaleString()} total votes`, 70, 540);

    // Download
    const link = document.createElement('a');
    link.download = `poll-results-${pollId}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  if (!isOpen) return null;

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Calculate max votes for chart scaling
  const maxDayVotes = Math.max(...voteTrends.map(t => t.totalVotes), 1);

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div 
          className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-500 to-cyan-500 px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <i className="ri-bar-chart-grouped-line text-white text-2xl"></i>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Poll Analytics</h2>
                  <p className="text-white/80 text-sm line-clamp-1">{pollTitle}</p>
                </div>
              </div>
              <button 
                onClick={onClose}
                className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {/* View Tabs */}
            <div className="flex gap-2 mt-4">
              {[
                { id: 'overview', label: 'Overview', icon: 'ri-dashboard-line' },
                { id: 'trends', label: 'Voting Trends', icon: 'ri-line-chart-line' },
                { id: 'breakdown', label: 'Breakdown', icon: 'ri-pie-chart-line' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveView(tab.id as any)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer flex items-center gap-2 ${
                    activeView === tab.id
                      ? 'bg-white text-blue-600'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  <i className={tab.icon}></i>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
            {/* Overview View */}
            {activeView === 'overview' && (
              <div className="space-y-6">
                {/* Key Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-4 border border-blue-100">
                    <div className="flex items-center gap-2 text-blue-600 mb-2">
                      <i className="ri-group-line"></i>
                      <span className="text-xs font-medium">Total Votes</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{totalVotes.toLocaleString()}</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-100">
                    <div className="flex items-center gap-2 text-emerald-600 mb-2">
                      <i className="ri-trophy-line"></i>
                      <span className="text-xs font-medium">Leading</span>
                    </div>
                    <p className="text-lg font-bold text-gray-900 line-clamp-1">{stats.leader.text}</p>
                    <p className="text-sm text-emerald-600">{Math.round(stats.leaderPercentage)}%</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-100">
                    <div className="flex items-center gap-2 text-amber-600 mb-2">
                      <i className="ri-arrow-up-circle-line"></i>
                      <span className="text-xs font-medium">Lead Margin</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">+{Math.round(stats.leadMargin)}%</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-100">
                    <div className="flex items-center gap-2 text-purple-600 mb-2">
                      <i className="ri-calendar-line"></i>
                      <span className="text-xs font-medium">Avg Daily</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{Math.round(stats.avgDailyVotes)}</p>
                  </div>
                </div>

                {/* Results Chart */}
                <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <i className="ri-bar-chart-horizontal-line text-blue-500"></i>
                    Current Results
                  </h3>
                  <div className="space-y-3">
                    {stats.sortedOptions.map((option, index) => {
                      const percentage = totalVotes > 0 ? (option.votes / totalVotes) * 100 : 0;
                      const colors = [
                        'from-blue-500 to-cyan-500',
                        'from-emerald-500 to-teal-500',
                        'from-amber-500 to-orange-500',
                        'from-rose-500 to-pink-500',
                        'from-purple-500 to-indigo-500',
                        'from-gray-500 to-slate-500'
                      ];
                      
                      return (
                        <div key={option.id} className="relative">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              {index === 0 && <i className="ri-trophy-fill text-amber-500"></i>}
                              <span className="text-sm font-medium text-gray-800">{option.text}</span>
                            </div>
                            <span className="text-sm font-bold text-gray-700">
                              {Math.round(percentage)}% ({option.votes})
                            </span>
                          </div>
                          <div className="h-8 bg-gray-200 rounded-lg overflow-hidden">
                            <div 
                              className={`h-full bg-gradient-to-r ${colors[index % colors.length]} rounded-lg transition-all duration-500`}
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Poll Info */}
                <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    Created: {formatDate(createdAt)}
                  </span>
                  {endDate && (
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      Ends: {formatDate(endDate)}
                    </span>
                  )}
                  {allowMultiple && (
                    <span className="flex items-center gap-1 text-blue-600">
                      <i className="ri-checkbox-multiple-line"></i>
                      Multiple choice
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Trends View */}
            {activeView === 'trends' && (
              <div className="space-y-6">
                {/* Trend Chart */}
                <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <i className="ri-line-chart-line text-blue-500"></i>
                    Voting Activity Over Time
                  </h3>
                  
                  {/* Simple Bar Chart */}
                  <div className="h-64 flex items-end justify-between gap-2 px-4">
                    {voteTrends.map((trend, index) => {
                      const height = (trend.totalVotes / maxDayVotes) * 100;
                      return (
                        <div key={index} className="flex-1 flex flex-col items-center group">
                          <div className="relative w-full flex justify-center mb-2">
                            <div 
                              className="w-full max-w-12 bg-gradient-to-t from-blue-500 to-cyan-400 rounded-t-lg transition-all duration-300 group-hover:from-blue-600 group-hover:to-cyan-500 cursor-pointer"
                              style={{ height: `${Math.max(height, 5)}%`, minHeight: '8px' }}
                            >
                              {/* Tooltip */}
                              <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                {trend.totalVotes} votes
                              </div>
                            </div>
                          </div>
                          <span className="text-xs text-gray-500">{formatDate(trend.date)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Option Trends */}
                <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <i className="ri-stack-line text-emerald-500"></i>
                    Option Performance Over Time
                  </h3>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Date</th>
                          {options.slice(0, 4).map(opt => (
                            <th key={opt.id} className="text-center py-2 px-3 font-medium text-gray-600">
                              {opt.text.substring(0, 15)}{opt.text.length > 15 ? '...' : ''}
                            </th>
                          ))}
                          <th className="text-center py-2 px-3 font-medium text-gray-600">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {voteTrends.map((trend, index) => (
                          <tr key={index} className="border-b border-gray-100 hover:bg-gray-100/50">
                            <td className="py-2 px-3 text-gray-700">{formatDate(trend.date)}</td>
                            {options.slice(0, 4).map(opt => (
                              <td key={opt.id} className="text-center py-2 px-3">
                                <span className={`inline-block px-2 py-0.5 rounded ${
                                  trend.votes[opt.id] > 0 ? 'bg-blue-100 text-blue-700' : 'text-gray-400'
                                }`}>
                                  +{trend.votes[opt.id] || 0}
                                </span>
                              </td>
                            ))}
                            <td className="text-center py-2 px-3 font-medium text-gray-900">
                              {trend.totalVotes}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Peak Activity */}
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-5 border border-amber-200">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                      <i className="ri-fire-line text-amber-600 text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Peak Voting Day</h4>
                      <p className="text-sm text-gray-600">
                        {formatDate(stats.peakDay.date)} with <span className="font-bold text-amber-600">{stats.peakDay.totalVotes} votes</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Breakdown View */}
            {activeView === 'breakdown' && (
              <div className="space-y-6">
                {/* Pie Chart Visualization */}
                <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <i className="ri-pie-chart-line text-purple-500"></i>
                    Vote Distribution
                  </h3>
                  
                  <div className="flex flex-col md:flex-row items-center gap-8">
                    {/* Simple Donut Chart */}
                    <div className="relative w-48 h-48">
                      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                        {(() => {
                          let cumulativePercent = 0;
                          const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#6b7280'];
                          
                          return stats.sortedOptions.map((option, index) => {
                            const percent = totalVotes > 0 ? (option.votes / totalVotes) * 100 : 0;
                            const strokeDasharray = `${percent} ${100 - percent}`;
                            const strokeDashoffset = -cumulativePercent;
                            cumulativePercent += percent;
                            
                            return (
                              <circle
                                key={option.id}
                                cx="50"
                                cy="50"
                                r="40"
                                fill="none"
                                stroke={colors[index % colors.length]}
                                strokeWidth="20"
                                strokeDasharray={strokeDasharray}
                                strokeDashoffset={strokeDashoffset}
                                className="transition-all duration-500"
                              />
                            );
                          });
                        })()}
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-gray-900">{totalVotes}</p>
                          <p className="text-xs text-gray-500">votes</p>
                        </div>
                      </div>
                    </div>

                    {/* Legend */}
                    <div className="flex-1 space-y-2">
                      {stats.sortedOptions.map((option, index) => {
                        const percent = totalVotes > 0 ? (option.votes / totalVotes) * 100 : 0;
                        const colors = ['bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500', 'bg-purple-500', 'bg-gray-500'];
                        
                        return (
                          <div key={option.id} className="flex items-center gap-3">
                            <div className={`w-4 h-4 rounded ${colors[index % colors.length]}`}></div>
                            <span className="flex-1 text-sm text-gray-700">{option.text}</span>
                            <span className="text-sm font-medium text-gray-900">{Math.round(percent)}%</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Detailed Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {stats.sortedOptions.map((option, index) => {
                    const percent = totalVotes > 0 ? (option.votes / totalVotes) * 100 : 0;
                    const colors = [
                      { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-600' },
                      { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-600' },
                      { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-600' },
                      { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-600' },
                      { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-600' },
                      { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-600' }
                    ];
                    const color = colors[index % colors.length];
                    
                    return (
                      <div key={option.id} className={`${color.bg} rounded-xl p-4 border ${color.border}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {index === 0 && <i className="ri-medal-line text-amber-500"></i>}
                            <span className="font-medium text-gray-900">{option.text}</span>
                          </div>
                          <span className={`text-lg font-bold ${color.text}`}>{Math.round(percent)}%</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <span><i className="ri-user-line mr-1"></i>{option.votes} votes</span>
                          <span><i className="ri-bar-chart-line mr-1"></i>Rank #{index + 1}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Footer with Share Options */}
          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-sm text-gray-600">
                <i className="ri-share-line mr-1"></i>
                Share these results with your community
              </p>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCopyResults}
                  className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium text-gray-700 transition-colors cursor-pointer flex items-center gap-2"
                >
                  <i className="ri-file-copy-line"></i>
                  Copy
                </button>
                <button
                  onClick={handleDownloadImage}
                  className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium text-gray-700 transition-colors cursor-pointer flex items-center gap-2"
                >
                  <i className="ri-download-line"></i>
                  Image
                </button>
                <button
                  onClick={handleShareToTwitter}
                  className="w-10 h-10 bg-black hover:bg-gray-800 rounded-lg flex items-center justify-center text-white transition-colors cursor-pointer"
                  title="Share on X/Twitter"
                >
                  <i className="ri-twitter-x-line"></i>
                </button>
                <button
                  onClick={handleShareToFacebook}
                  className="w-10 h-10 bg-blue-600 hover:bg-blue-700 rounded-lg flex items-center justify-center text-white transition-colors cursor-pointer"
                  title="Share on Facebook"
                >
                  <i className="ri-facebook-fill"></i>
                </button>
                <button
                  onClick={handleShareToWhatsApp}
                  className="w-10 h-10 bg-green-500 hover:bg-green-600 rounded-lg flex items-center justify-center text-white transition-colors cursor-pointer"
                  title="Share on WhatsApp"
                >
                  <i className="ri-whatsapp-line"></i>
                </button>
                <button
                  onClick={handleShareResults}
                  className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 rounded-lg text-sm font-medium text-white transition-colors cursor-pointer flex items-center gap-2"
                >
                  <i className="ri-share-forward-line"></i>
                  More
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          title={`Poll Results: ${pollTitle}`}
          url={`${window.location.origin}/forum?poll=${pollId}`}
        />
      )}
    </>
  );
}
